<template>
  <div id="app" class="app">
    <div class="app-container">
      <div class="wrapper">
        <div class="wrapper-top" :class="{ 'wrapper-top--bare': isConsultationPage }">
          <main-header
            v-show="!isConsultationPage"
            :headerData="headerContactsBlock || []"
          />
          <transition name="bounce">
            <router-view :key="$route.params.slug || $route.path"/>
          </transition>
        </div>
        <div id="html-overlay"></div>
        <div class="wrapper-bottom">
          <main-footer
            v-show="!isConsultationPage"
            :footerData="footerContactsBlock || []"
          />
        </div>
      </div>
    </div>
    <hidden/>
    <compare-added-popup/>
  </div>
</template>

<style src="@/assets/scss/main.scss" lang="scss"></style>
<!--<script src="src/utils/PrefixFree.js"></script>-->
<script>

import {mapGetters, mapActions, mapMutations} from 'vuex';
import Hidden from '@/components/hidden/index.vue';
import MainHeader from '@/components/header/index.vue';
import MainFooter from '@/components/footer/index.vue';
import CompareAddedPopup from '@/components/compare-added-popup/index.vue';
import {isMobile, isIPad} from '@/helpers/variables/index'
import purchaseMixin from '@/mixins/purchaseMixin'
import { installPurchaseDebugLogger } from '@/utils/purchaseDebug'

export default {
  name: 'app',
  components: {
    Hidden,
    MainHeader,
    MainFooter,
    CompareAddedPopup
  },
  mixins: [purchaseMixin],
  computed: {
    ...mapGetters({
      isAuthenticated: `auth/isAuthenticated`,
      variables: `setting/variables`,
      headerDates: 'setting/headerDates',
      footerDates: 'setting/footerDates',
      currentLanguage: 'currentLanguage',
    }),
    headerContactsBlock() {
      const h = this.headerDates;
      if (!Array.isArray(h) || !h[0] || h[0].contacts == null) {
        return null;
      }
      return h[0].contacts;
    },
    footerContactsBlock() {
      const f = this.footerDates;
      if (!Array.isArray(f) || !f[0] || f[0].contacts == null) {
        return null;
      }
      return f[0].contacts;
    },
    isConsultationPage() {
      return this.$route.name === 'consultation';
    },
  },
  watch: {
    '$route': {
      immediate: true,
      handler(to, from) {
        const isConsultation = to.name === 'consultation';
        window.__isConsultationPage = isConsultation;
        document.body.classList.toggle('cp-page', isConsultation);
        if (isConsultation) {
          window.__b24WidgetLoaded = true;
          this.hideB24Widget();
        } else {
          // Виджет включается обратно только при уходе со страницы
          // консультации — то есть при настоящем переходе, когда есть from.
          // Раньше этот же код выполнялся и на первой отрисовке (watcher
          // объявлен immediate) и сбрасывал флаг __b24WidgetLoaded, из-за
          // чего Bitrix24 подтягивался сразу после старта приложения в обход
          // отложенной загрузки в index.html. Проверка в браузере показала
          // ровно это: виджет приезжал раньше, чем человек что-либо сделал.
          const leftConsultation = from && from.name === 'consultation';

          if (leftConsultation) {
            window.__b24WidgetLoaded = false;

            if (typeof window.loadB24Widget === 'function') {
              window.loadB24Widget();
            }
          }

          this.$nextTick(() => {
            this.resetLayoutChromeStyles();
            window.dispatchEvent(new Event('resize'));
          });
        }
      },
    },
    currentLanguage: {
      immediate: true,
      handler(language) {
        if (!language || this.isConsultationPage) {
          return;
        }

        this.ensureContactsLoaded();
      },
    },
  },
  mounted() {
    // Оба логгера — отладочные. Второй до этой правки работал и в проде:
    // он подменял dataLayer.push на каждой странице и писал в консоль
    // покупателя каждое событие электронной торговли. Помимо шума это
    // лишний слой поверх горячего пути аналитики.
    if (process.env.NODE_ENV === 'development') {
      installPurchaseDebugLogger()
      this.initViewItemDebugLogger()
    }
    if (!isMobile.any()) {
      document.querySelector('body').classList.add('hover');
    }
    if (!isIPad.iOS()) {
      document.querySelector('body').classList.add('iPad');
    }
    
    // Check for payment success and track purchase
    this.checkPaymentSuccessAndTrack();
  },
  methods: {
    ...mapMutations({}),
    ...mapActions({
      getContacts: 'setting/GET_MAIN_CONTACTS'
    }),
    ensureContactsLoaded() {
      this.getContacts({ type: 'header' });
      this.getContacts({ type: 'footer' });
    },
    hideB24Widget() {
      const selectors = [
        '[class*="b24-"]',
        '[id*="b24"]',
        '#b24-widget-button',
        'iframe[src*="bitrix"]',
        'iframe[src*="bplbe"]',
      ].join(',');
      document.querySelectorAll(selectors).forEach((el) => {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      });
    },
    resetLayoutChromeStyles() {
      ['.main-header', '.main-footer'].forEach((selector) => {
        const el = document.querySelector(selector);
        if (!el) return;
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('pointer-events');
      });
      document.documentElement.style.removeProperty('--main-header-height');
    },
    initViewItemDebugLogger() {
      if (typeof window === 'undefined') {
        return
      }
      if (!Array.isArray(window.dataLayer)) {
        window.dataLayer = []
      }
      if (window.__viewItemDebugHookInstalled) {
        return
      }
      const originalPush = window.dataLayer.push.bind(window.dataLayer)
      window.dataLayer.push = (...args) => {
        args.forEach((payload) => {
          if (!payload || !payload.event) {
            return
          }
          const trackedEvents = ['view_item', 'view_item_list', 'add_to_cart']
          if (trackedEvents.includes(payload.event)) {
            console.warn(`[GA][GLOBAL][${payload.event}][DETECTED]`, payload)
          }
        })
        return originalPush(...args)
      }
      window.__viewItemDebugHookInstalled = true
    },
  }
}
</script>

